import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { FormError } from '../components/ui/FormError.js';
import fieldStyles from '../components/ui/fields.module.css';
import { TextField } from '../components/ui/TextField.js';
import { authClient } from '../lib/auth-client.js';
import { authErrorMessage } from '../lib/auth-errors.js';

const schema = z
    .object({
        password: z.string().min(10, 'Use at least 10 characters'),
        confirm: z.string(),
    })
    .refine((values) => values.password === values.confirm, {
        path: ['confirm'],
        message: 'Passwords do not match',
    });

type Values = z.infer<typeof schema>;

export function ResetPasswordPage(): ReactElement {
    const [params] = useSearchParams();
    const token = params.get('token');
    const linkError = params.get('error');
    const [serverError, setServerError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    const form = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { password: '', confirm: '' },
    });

    async function onSubmit(values: Values): Promise<void> {
        if (token === null) {
            return;
        }
        setServerError(null);
        const result = await authClient.resetPassword({ newPassword: values.password, token });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not reset the password'));
            return;
        }
        setDone(true);
    }

    if (done) {
        return (
            <>
                <h1>Password updated</h1>
                <p>You can sign in with your new password. Other devices have been signed out.</p>
                <p className={styles.links}>
                    <Link to={routePaths.signIn}>Sign in</Link>
                </p>
            </>
        );
    }

    if (token === null || linkError !== null) {
        return (
            <>
                <h1>Link not valid</h1>
                <p>
                    This reset link has expired or was already used. Request a new one to continue.
                </p>
                <p className={styles.links}>
                    <Link to={routePaths.forgotPassword}>Request a new link</Link>
                </p>
            </>
        );
    }

    return (
        <>
            <h1>Choose a new password</h1>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    void form.handleSubmit(onSubmit)(event);
                }}
            >
                <TextField
                    label="New password"
                    type="password"
                    autoComplete="new-password"
                    error={form.formState.errors.password?.message}
                    {...form.register('password')}
                />
                <TextField
                    label="Confirm new password"
                    type="password"
                    autoComplete="new-password"
                    error={form.formState.errors.confirm?.message}
                    {...form.register('confirm')}
                />
                <FormError message={serverError} />
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Save password
                </Button>
            </form>
        </>
    );
}
